import React, { useCallback, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';

import { css } from '@emotion/react';
import { styled, useTheme } from '@mui/material/styles';
import AccountButton from '../AccountButton';
import LoginModal from '../LoginModal';
import { useUserContext } from 'context/userContext';
import { NAVBAR } from 'data/navbar';
import { Link } from 'react-router-dom';
import { SOCIAL } from 'data/socials';
import { useThemeMode } from 'context/themeContext';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

const Row = styled('div')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	padding: '20px 60px',
	borderRight: `1px solid ${theme.palette.atlas.border.default}`,

	'@media (max-width: 768px)': {
		padding: '12px 16px',
	},
}));

const navContainer = css`
	max-height: 82px;
	height: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;

	@media (max-width: 768px) {
		max-height: 60px;
	}
`;

const Image = styled('img')`
	width: 24px;
	height: 24px;
	position: relative;

	:hover {
		cursor: pointer;
	}

	& img:hover {
		filter: invert(44%) sepia(99%) saturate(6152%) hue-rotate(220deg)
			brightness(95%) contrast(106%);
	}
`;

const StyledLink = styled(Link)(({ theme }) => ({
	color: theme.palette.text.primary,
	margin: theme.spacing(2),
	cursor: 'pointer',
	textDecoration: 'none',
	fontWeight: 600,
	marginRight: 2,
	'&:hover': {
		color: theme.palette.atlas.accent.navActive,
	},
	'&.active': {
		color: theme.palette.atlas.accent.navActive,
	},
}));

const StyledAnchor = styled('a')(({ theme }) => ({
	color: theme.palette.text.primary,
	cursor: 'pointer',
	textDecoration: 'none',
	fontWeight: 500,
	fontSize: 14,
	marginRight: '16px',
	'&:hover': {
		color: theme.palette.atlas.accent.navActive,
	},
	'&.active': {
		color: theme.palette.atlas.accent.navActive,
	},
}));

const ConnectButton = styled('div')`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 0 60px;

	@media (max-width: 768px) {
		padding: 0 12px;
	}
`;

const SocialAnchor = styled('a')`
	display: contents;
	width: 30px;
	height: 30px;
	position: relative;
	margin: 0 0 0 24px;
	padding: 0;
`;

const SocialWrapper = styled('div')(({ theme }) => ({
	display: 'flex',
	height: '100%',
	padding: 30,
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'center',
	borderRight: `1px solid ${theme.palette.atlas.border.default}`,
	borderLeft: `1px solid ${theme.palette.atlas.border.default}`,

	'@media (max-width: 768px)': {
		display: 'none',
	},
}));

const NavContainer = styled(Box)`
	${navContainer};
	background: var(--atlas-bg-app);
	border-bottom: 1px solid var(--atlas-border-default);
`;

const Navbar: React.ComponentType = () => {
	const theme = useTheme();
	const userContext = useUserContext();
	const { isLoggedIn } = userContext;
	const [loginModalOpen, setLoginModalOpen] = useState(false);
	const { mode, toggleMode } = useThemeMode();
	const handleToggleMode = useCallback(() => {
		toggleMode();
	}, [toggleMode]);
	const logoSrc =
		mode === 'dark'
			? '/images/rndm-logo.png'
			: '/images/rndm-logo-light.png';

	const LightModeButton = useCallback(() => {
		return (
			<IconButton
				aria-label="Toggle theme"
				onClick={handleToggleMode}
				sx={{
					color: theme.palette.text.primary,
					backgroundColor: theme.palette.atlas.background.surface,
					borderRadius: '12px',
					ml: 2,
					mr: 2,
					'&:hover': {
						backgroundColor:
							theme.palette.atlas.background.surfaceHover,
					},
				}}
			>
				{mode === 'dark' ? (
					<LightModeIcon fontSize="small" />
				) : (
					<DarkModeIcon fontSize="small" />
				)}
			</IconButton>
		);
	}, [mode, handleToggleMode]);

	return (
		<>
			<NavContainer
				sx={{
					position: 'fixed',
					display: 'flex',
					justifyContent: 'space-between',
					zIndex: 3000,
					width: '100%',
				}}
			>
				<Row>
					<a href={'/'} style={{ display: 'contents' }}>
						<img
							src={logoSrc}
							height={42}
							width={45}
							alt={'movement'}
							style={{
								height: 'auto',
								maxHeight: '42px',
								width: 'auto',
								maxWidth: '45px',
							}}
						/>
					</a>
				</Row>

				<Box display="flex" alignItems="center">
					<SocialWrapper>
						{NAVBAR.map((d) => {
							return d.target ? (
								<StyledAnchor
									key={d.to}
									target={d.target}
									href={d.to}
								>
									{d.name}
								</StyledAnchor>
							) : (
								<StyledLink key={d.to} to={d.to}>
									{d.name}
								</StyledLink>
							);
						})}
						<Box width={16} />
						<div
							style={{
								display: 'contents',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							{SOCIAL.map((social) => {
								const iconSrc =
									mode === 'dark'
										? social.iconDark
										: social.iconLight;

								return (
									<SocialAnchor
										href={social.url}
										target="_blank"
										key={social.name}
									>
										{iconSrc && (
											<Image
												src={iconSrc}
												alt={social.name}
												width={24}
												height={24}
												style={{
													marginLeft: 24,
													padding: 0,
												}}
											/>
										)}
										{!iconSrc && (
											<div
												style={{
													display: 'flex',
													cursor: 'pointer',
												}}
											>
												<Typography
													color="textPrimary"
													style={{
														marginLeft: 24,
														padding: 0,
													}}
												>
													{social.name}
												</Typography>
											</div>
										)}
									</SocialAnchor>
								);
							})}
							<LightModeButton />
						</div>
					</SocialWrapper>
					<ConnectButton>
						{isLoggedIn ? (
							<AccountButton />
						) : (
							<Button
								variant="contained"
								onClick={() => setLoginModalOpen(true)}
								sx={{
									backgroundColor: theme.palette.primary.main,
									whiteSpace: 'nowrap',
									color: '#fff',
									padding: '12px 24px',
									fontFamily: 'AmigaForever',
									fontWeight: 600,
									fontSize: '12px',
									letterSpacing: '0.15em',
									textTransform: 'uppercase',
									boxShadow: 'none',
									clipPath:
										'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)',
									'&:hover': {
										backgroundColor:
											theme.palette.primary.dark,
										boxShadow: 'none',
									},
									'&:disabled': {
										backgroundColor:
											theme.palette.action
												.disabledBackground,
										boxShadow: 'none',
										color: theme.palette.text.disabled,
									},
									'@media (max-width: 768px)': {
										padding: '8px 12px',
										fontSize: '11px',
										letterSpacing: '0.08em',
										minWidth: 'auto',
										clipPath:
											'polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px)',
									},
								}}
							>
								Connect Wallet
							</Button>
						)}
					</ConnectButton>
				</Box>
			</NavContainer>

			<LoginModal
				open={loginModalOpen}
				onClose={() => setLoginModalOpen(false)}
			/>
		</>
	);
};

export { Navbar };
